import { useState } from 'react'
import { uploadPulseFile } from './pulseApi'
import { PULSE_FILE_CATEGORIES } from './pulseAddFiles.js'

export function PulseAddFilesButton({
  projectId,
  onUploaded,
}: {
  projectId: number
  onUploaded?: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="btn b-ol btn-sm" data-testid="pulse-add-files" onClick={() => setOpen(true)}>
        Add files
      </button>
      {open && (
        <PulseAddFiles
          projectId={projectId}
          onClose={() => setOpen(false)}
          onUploaded={() => { setOpen(false); onUploaded?.() }}
        />
      )}
    </>
  )
}

export default function PulseAddFiles({
  projectId,
  onClose,
  onUploaded,
}: {
  projectId: number
  onClose: () => void
  onUploaded: () => void
}) {
  const [docType, setDocType] = useState('photo')
  const [notes, setNotes] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (!files.length || busy) return
    setBusy(true)
    setError(null)
    try {
      for (const file of files) {
        await uploadPulseFile(projectId, file, { docType, notes })
      }
      onUploaded()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="pulse-add-files-backdrop"
      data-testid="pulse-add-files-modal"
      onClick={onClose}
    >
      <div className="pulse-add-files-dialog" role="dialog" aria-label="Add files" onClick={(e) => e.stopPropagation()}>
        <h2>Add files</h2>
        <label>
          Category
          <select data-testid="pulse-add-files-type" value={docType} onChange={(e) => setDocType(e.target.value)}>
            {PULSE_FILE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
        <label>
          Files
          <input
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.xlsx,.jpg,.jpeg,.png"
            data-testid="pulse-add-files-file"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </label>
        {files.length > 0 && (
          <ul className="pulse-add-files-list">
            {files.map((f) => <li key={`${f.name}-${f.size}`}>{f.name}</li>)}
          </ul>
        )}
        <label>
          Notes
          <textarea
            data-testid="pulse-add-files-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </label>
        {error && <p className="pulse-add-files-error">{error}</p>}
        <div className="pulse-add-files-actions">
          <button type="button" className="btn b-ol btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn b-dk btn-sm"
            data-testid="pulse-add-files-submit"
            onClick={() => void submit()}
            disabled={!files.length || busy}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  )
}
